import type { OccupancyBookingSpan, OccupancySpanKind } from '@sea-snag/shared';

/**
 * Which bookings a displayed grid week actually contains.
 *
 * A cell shows one solid colour, so on its own it cannot say which charter it
 * came from or how much of the week was sold. The detail popover answers that
 * from the booking spans sent with the grid, and a week holding several of them
 * lists them all rather than the cell trying to express it.
 *
 * Spans are half-open: `endDate` is the turnaround day, so a charter running
 * 16 → 23 May occupies the nights of the 16th to the 22nd. A charter ending the
 * morning a week begins therefore contributes nothing to it — reading the end as
 * inclusive put a phantom booked day in the following week.
 */

const DAY_MS = 86_400_000;
const ms = (isoDate: string): number => Date.parse(`${isoDate}T00:00:00Z`);

/**
 * Cell-colour precedence, so the first span listed is the one the cell's colour
 * came from. Any other order makes the popover look like it contradicts the
 * cell it opened from.
 */
const KIND_ORDER: Record<OccupancySpanKind, number> = {
  regatta: 0,
  booked: 1,
  owner: 2,
  external: 3,
  option: 4,
  service: 5,
  other: 6,
};

export interface SpanInWeek {
  span: OccupancyBookingSpan;
  /** Nights this span occupies within the displayed week — 1 to 7. */
  daysInWeek: number;
}

/**
 * How long the whole booking is, in nights.
 *
 * The popover used to quote only `daysInWeek`, which answers "how much of this
 * week was sold" — so a fortnight clicked on its first week said "7 days" and
 * read as if the charter were a week long. The dates beside it said otherwise.
 */
export function spanLengthDays(span: OccupancyBookingSpan): number {
  return Math.round((ms(span.endDate) - ms(span.startDate)) / DAY_MS);
}

export function spansInWeek(
  spans: OccupancyBookingSpan[],
  weekStart: string,
  weekEnd: string,
  boatPid?: string
): SpanInWeek[] {
  const from = ms(weekStart);
  // weekEnd is the last night, so the exclusive bound is the morning after.
  const to = ms(weekEnd) + DAY_MS;

  const hits: SpanInWeek[] = [];
  for (const span of spans) {
    if (boatPid !== undefined && span.boatPid !== boatPid) continue;
    const overlap = Math.min(ms(span.endDate), to) - Math.max(ms(span.startDate), from);
    if (overlap <= 0) continue;
    hits.push({ span, daysInWeek: Math.round(overlap / DAY_MS) });
  }

  return hits.sort(
    (a, b) =>
      KIND_ORDER[a.span.kind] - KIND_ORDER[b.span.kind] ||
      a.span.startDate.localeCompare(b.span.startDate)
  );
}
